import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { LanguageService } from '@core/services/language/language.service';
import { ImagePaintDialogComponent } from '@shared/components/image-paint-dialog/image-paint-dialog.component';

/**
 * Entrada de adjuntos desde el portapapeles o dibujando desde cero.
 * Reglas: UNA sola imagen pegada abre el editor de paint antes de adjuntarse;
 * varias imágenes y/o archivos se adjuntan todos tal cual, sin editor;
 * "dibujar" abre un lienzo en blanco.
 * Devuelve File[] listos para subir — la subida la hace quien llama.
 */
@Injectable({ providedIn: 'root' })
export class AttachmentIntakeService {
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly language = inject(LanguageService);

  /**
   * Lee el portapapeles (botón "Pegar"): las imágenes pasan por el editor.
   * La API asíncrona del navegador solo expone imágenes/texto; los archivos
   * copiados del sistema llegan por el evento paste (Cmd/Ctrl+V), no por aquí.
   */
  async pasteFromClipboard(): Promise<File[]> {
    let items: ClipboardItems;
    try {
      items = await navigator.clipboard.read();
    } catch {
      this.hint('gdp.attachments.paste-denied');
      return [];
    }
    const images: Blob[] = [];
    for (const item of items) {
      const imageType = item.types.find(type => type.startsWith('image/'));
      if (imageType) {
        images.push(await item.getType(imageType));
      }
    }
    if (images.length === 0) {
      this.hint('gdp.attachments.paste-empty');
      return [];
    }
    if (images.length === 1) {
      const file = await this.paintOver(images[0], this.stampedName('pegado'));
      return file ? [file] : [];
    }
    // Varias imágenes: se adjuntan directamente, sin pasar por el editor.
    return images.map((blob, i) =>
      new File([blob], this.stampedName(`pegado-${i + 1}`), { type: blob.type || 'image/png' })
    );
  }

  /**
   * Procesa un evento paste nativo (Cmd/Ctrl+V): se adjunta TODO lo que haya
   * (n imágenes + m archivos). El editor solo se abre si la única imagen del
   * portapapeles es exactamente una; con varias, van directas.
   */
  async handlePasteEvent(event: ClipboardEvent): Promise<File[]> {
    const transfer = event.clipboardData;
    if (!transfer) {
      return [];
    }
    let files = Array.from(transfer.files);
    if (files.length === 0) {
      // Algunos navegadores exponen el contenido solo vía items (p. ej. capturas)
      files = Array.from(transfer.items)
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter((file): file is File => file !== null);
    }
    if (files.length === 0) {
      return [];
    }
    event.preventDefault();

    const images = files.filter(file => file.type.startsWith('image/'));
    const others = files.filter(file => !file.type.startsWith('image/'));
    if (images.length === 1) {
      const edited = await this.paintOver(images[0], this.stampedName('pegado'));
      return edited ? [...others, edited] : others;
    }
    return files;
  }

  /** Abre el editor con lienzo en blanco ("Dibujar"). */
  async draw(): Promise<File[]> {
    const fileName = this.stampedName('dibujo');
    const blob = await firstValueFrom(
      this.dialog.open(ImagePaintDialogComponent, {
        data: { fileName, blankSize: { width: 1200, height: 800 } },
        maxWidth: '94vw',
        disableClose: true
      }).afterClosed()
    );
    return blob ? [new File([blob], fileName, { type: 'image/png' })] : [];
  }

  /** Abre el editor sobre una imagen y devuelve el PNG resultante como File. */
  private async paintOver(image: Blob, fileName: string): Promise<File | null> {
    const imageUrl = URL.createObjectURL(image);
    try {
      const blob = await firstValueFrom(
        this.dialog.open(ImagePaintDialogComponent, {
          data: { imageUrl, fileName },
          maxWidth: '94vw',
          disableClose: true
        }).afterClosed()
      );
      return blob ? new File([blob], fileName, { type: 'image/png' }) : null;
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  }

  private stampedName(prefix: string): string {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    return `${prefix}-${stamp}.png`;
  }

  private hint(messageKey: string): void {
    this.snackBar.open(this.language.translate(messageKey), undefined, { duration: 3500 });
  }
}
